/**
 * Iter 261 — Prévisualisation d'un import de programme (OCR + IA) avant création.
 *
 * Reçoit le draft retourné par POST /lms/programs/import-preview, permet de
 * corriger chaque champ (titre, durées, prix, sections, modules), puis confirme
 * via POST /lms/programs/import-confirm.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Loader2, Check, Sparkles, FileText, Trash2, Plus, ChevronDown, ChevronRight, ListChecks,
} from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";
const taCls = `${inputCls} text-sm min-h-[70px]`;

const SECTION_FIELDS = [
  { key: "section_modalites", label: "Modalités (durée, lieu, rythme, tarifs...)" },
  { key: "section_contenu_pedagogique", label: "Contenu pédagogique" },
  { key: "section_suivi_execution", label: "Suivi de l'exécution" },
  { key: "section_modalites_certification", label: "Modalités d'évaluation / certification" },
  { key: "section_qualite", label: "Qualité & accessibilité" },
];

export default function ImportPreviewDialog({ draft: initialDraft, onClose, onConfirm, confirming }) {
  const [d, setD] = useState({ ...initialDraft });
  const [showSections, setShowSections] = useState(false);
  const [openModules, setOpenModules] = useState({ 0: true });

  const set = (key, value) => setD(prev => ({ ...prev, [key]: value }));
  const setModule = (idx, key, value) =>
    setD(prev => ({ ...prev, modules: prev.modules.map((m, i) => (i === idx ? { ...m, [key]: value } : m)) }));
  const removeModule = (idx) =>
    setD(prev => ({ ...prev, modules: prev.modules.filter((_, i) => i !== idx) }));
  const addModule = () =>
    setD(prev => ({ ...prev, modules: [...(prev.modules || []), { title: `Module ${(prev.modules || []).length + 1}`, content: "", duration_min: 0 }] }));

  const confirm = async () => {
    if (!(d.title || "").trim()) { toast.error("Titre requis"); return; }
    await onConfirm(d);
  };

  const nbModules = (d.modules || []).length;
  const isAI = d.parse_method === "ia";

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !confirming) onClose(); }}>
      <DialogContent className="max-w-3xl bg-[#0b0811] border border-white/10 text-white max-h-[88vh] flex flex-col p-0" data-testid="import-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Vérifiez l'import avant création
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase flex items-center gap-1 ${isAI ? "bg-[#f7c935]/15 text-[#f7c935]" : "bg-slate-500/20 text-slate-400"}`} data-testid="preview-parse-method">
              {isAI ? <Sparkles className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
              {isAI ? "Analyse IA" : "Heuristique"}
            </span>
            <span className="text-[10px] text-slate-500 font-normal truncate">{d.filename}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Identité */}
          <div className="space-y-2">
            <Input value={d.title || ""} onChange={e => set("title", e.target.value)} placeholder="Titre du programme *" className={`${inputCls} font-bold`} data-testid="preview-title" />
            <Input value={d.sous_titre || ""} onChange={e => set("sous_titre", e.target.value)} placeholder="Sous-titre / certification" className={inputCls} data-testid="preview-sous-titre" />
          </div>

          {/* Chiffres clés */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <LabeledInput label="Durée (heures)" type="number" value={d.duration_hours || 0} onChange={v => set("duration_hours", v)} testId="preview-duration-hours" />
            <LabeledInput label="Durée (jours)" type="number" value={d.duration_days || 0} onChange={v => set("duration_days", v)} testId="preview-duration-days" />
            <LabeledInput label="Prix HT (€)" type="number" value={d.price_ht || 0} onChange={v => set("price_ht", v)} testId="preview-price" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Modalité</div>
              <select value={d.modality || "presentiel"} onChange={e => set("modality", e.target.value)} className={`${inputCls} w-full px-2 py-2 rounded text-sm`} data-testid="preview-modality">
                <option value="presentiel">Présentiel</option>
                <option value="distanciel">Distanciel</option>
                <option value="mixte">Mixte</option>
                <option value="elearning">E-learning</option>
              </select>
            </div>
          </div>

          {/* Description / objectifs / public / prérequis */}
          <LabeledTextarea label="Description" value={d.description || ""} onChange={v => set("description", v)} testId="preview-description" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
              <ListChecks className="w-3 h-3" /> Objectifs pédagogiques (1 par ligne — {(d.objectives || []).length} détectés)
            </div>
            <Textarea
              value={(d.objectives || []).join("\n")}
              onChange={e => set("objectives", e.target.value.split("\n").filter(x => x.trim()))}
              className={`${taCls} min-h-[110px]`}
              data-testid="preview-objectives"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LabeledTextarea label="Public visé" value={d.target_audience || ""} onChange={v => set("target_audience", v)} testId="preview-target-audience" />
            <LabeledTextarea label="Prérequis" value={d.prerequisites || ""} onChange={v => set("prerequisites", v)} testId="preview-prerequisites" />
          </div>

          {/* Sections détaillées (repliées) */}
          <button onClick={() => setShowSections(s => !s)} className="flex items-center gap-2 text-sm text-[#f7c935] hover:text-[#ffd948]" data-testid="preview-toggle-sections">
            {showSections ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Sections détaillées ({SECTION_FIELDS.filter(f => (d[f.key] || "").trim()).length}/{SECTION_FIELDS.length} remplies)
          </button>
          {showSections && (
            <div className="space-y-3 border-l-2 border-[#f7c935]/20 pl-4">
              {SECTION_FIELDS.map(f => (
                <LabeledTextarea key={f.key} label={f.label} value={d[f.key] || ""} onChange={v => set(f.key, v)} testId={`preview-${f.key}`} />
              ))}
            </div>
          )}

          {/* Modules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Modules / séquences détectés ({nbModules})</div>
              <button onClick={addModule} className="text-xs text-[#f7c935] hover:text-[#ffd948] flex items-center gap-1" data-testid="preview-add-module">
                <Plus className="w-3 h-3" /> Ajouter
              </button>
            </div>
            <div className="space-y-2">
              {(d.modules || []).map((m, i) => {
                const open = !!openModules[i];
                return (
                  <div key={i} className="bg-[#14101c] border border-white/10 rounded-lg" data-testid={`preview-module-${i}`}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button onClick={() => setOpenModules(o => ({ ...o, [i]: !open }))} className="text-slate-400 hover:text-white" data-testid={`preview-module-toggle-${i}`}>
                        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <Input value={m.title || ""} onChange={e => setModule(i, "title", e.target.value)} className={`${inputCls} flex-1 h-8 text-sm`} data-testid={`preview-module-title-${i}`} />
                      <button onClick={() => removeModule(i)} className="p-1.5 rounded hover:bg-red-500/10 text-red-400" title="Supprimer ce module" data-testid={`preview-module-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {open && (
                      <div className="px-3 pb-3">
                        <Textarea value={m.content || ""} onChange={e => setModule(i, "content", e.target.value)} className={`${taCls} min-h-[100px]`} placeholder="Contenu du module..." data-testid={`preview-module-content-${i}`} />
                      </div>
                    )}
                  </div>
                );
              })}
              {nbModules === 0 && <div className="text-xs text-slate-500 border border-dashed border-white/10 rounded-lg p-3 text-center">Aucun module détecté — vous pourrez en ajouter dans l'éditeur.</div>}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-white/10 bg-[#0b0811]">
          <span className="text-[11px] text-slate-500">{d.sections_filled || 0} section(s) · {nbModules} module(s)</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={confirming} data-testid="preview-cancel-btn">Annuler</Button>
            <Button onClick={confirm} disabled={confirming || !(d.title || "").trim()} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="preview-confirm-btn">
              {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Créer le programme
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const LabeledInput = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <Input type={type} value={value} onChange={e => onChange(type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)} className={inputCls} data-testid={testId} />
  </div>
);

const LabeledTextarea = ({ label, value, onChange, testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <Textarea value={value} onChange={e => onChange(e.target.value)} className={taCls} data-testid={testId} />
  </div>
);
